import { Content, Page } from '@backstage/core-components';
import { Box, Grid, Text } from '@backstage/ui';
import { useEmployeeProfile } from '@runway/devkit';
import { HomePageCard } from './HomePageCards';
import { HomeSection } from './HomeSection';
import { EmuMigrationCard } from './EmuMigrationCard';
import { HOMEPAGE_EXTERNAL_LINKS } from './links';
import { NewAnnouncementBanner } from '@backstage-community/plugin-announcements';
import '@runway/devkit/src/components/ChatButton/ChatButton.css';

type HomePageCardConfig = {
  href: string;
  title: string;
  description: string;
  target?: string;
};

const gettingStartedCards: HomePageCardConfig[] = [
  { href: '/hangar', title: 'Hangar', description: 'Get started quickly with Hangar' },
  { href: '/create', title: 'Create an application', description: 'Get started quickly with our many templates' },
  {
    href: '/migration-guidance',
    title: 'Migrate an existing application',
    description: 'Guidance on migrating your application to Runway Shared Clusters',
  },
  { href: '/access', title: 'Get access', description: 'Self-service access control for GitHub and more' },
  {
    href: '/announcements',
    title: 'View announcements',
    description: "View Runway's new features and announcements",
  },
];

const needHelpCards: HomePageCardConfig[] = [
  {
    href: '/docs/default/component/runway',
    title: 'Runway documentation',
    description: 'Read the latest information about Runway',
  },
  {
    href: HOMEPAGE_EXTERNAL_LINKS.requestFeature,
    title: 'Request a feature',
    description: 'Is Runway missing a vital feature? Let us know!',
    target: '_blank',
  },
  {
    href: HOMEPAGE_EXTERNAL_LINKS.slackChannel,
    title: 'Chat with us',
    description: 'Join our Slack channel to stay up to date and ask questions',
    target: '_blank',
  },
];

export const HomePage = () => {
  const { displayName: employeeName, jobTitle } = useEmployeeProfile({ withPrefix: false });

  return (
    <Page themeId="home">
      <Content noPadding>
        <Box px="7">
          <Box as="header" py="7" style={{ borderBottom: '1px solid var(--bui-border-1)' }}>
            <Text as="h1" variant="title-medium" weight="bold">
              {employeeName ? `Welcome, ${employeeName}! 🦃` : '🦃'}
            </Text>
            {jobTitle && <Text as="p">{jobTitle}</Text>}
          </Box>
          <NewAnnouncementBanner />
          <HomeSection title="In focus">
            <Grid.Root columns="12" gap="4">
              <Grid.Item colSpan="12">
                <EmuMigrationCard />
              </Grid.Item>
            </Grid.Root>
          </HomeSection>
          <HomeSection title="Getting started">
            <Grid.Root columns={{ initial: '1', sm: '2', lg: '3' }} gap="4">
              {gettingStartedCards.map(card => (
                <Grid.Item key={card.href}>
                  <HomePageCard {...card} />
                </Grid.Item>
              ))}
            </Grid.Root>
          </HomeSection>
          <HomeSection title="Need help?" marginY="8">
            <Grid.Root columns={{ initial: '1', sm: '2', lg: '3' }} gap="4">
              {needHelpCards.map(card => (
                <Grid.Item key={card.href}>
                  <HomePageCard {...card} />
                </Grid.Item>
              ))}
            </Grid.Root>
          </HomeSection>
        </Box>
      </Content>
    </Page>
  );
};
